/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { omit } from 'lodash';
import {
  CrudAddType,
  CrudListType,
  CrudRemoveType,
  CrudTemplate,
  ListColumns
} from '../../../organisms/crud/form-types';

import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { TFunction } from 'react-i18next';
import { formFields } from './form-fields';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import React from 'react';
import { CCIText } from 'app/types/cci';

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<CCIText> => [
  {
    title: translate('common.table.columns.id'),
    dataIndex: 'cci',
    key: 'cci',
    size: '1fr'
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    size: '200px',
    render: item => <DateTime date={item.createdAt} />
  },
  {
    title: translate('common.table.columns.updatedAt'),
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    size: '200px',
    render: item => <DateTime date={item.updatedAt} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  }
];

/**
 * List.
 */

const list = (translate: TFunction): CrudListType<CCIText> => ({
  columns: listColumns(translate),
  filters: listFilters(translate),
  enableExport: true,
  resolveParams: (params: any, item: any) => {
    console.log({
      ...params,
      ...item
    });

    return {
      ...params,
      ...item
    };
  },
  route: appRoutes.dashboard.medical.ccisCodes,
  addButton: translate('common.actions.add'),
  canEdit: false,
  canRemove: true,
  key: ['ccis'],
  pagination: {
    pageSize: 25,
    pageSizeOptions: [10, 25, 50, 100, 500, 1000]
  },
  endpoint: apiEndpoints.ccisCodes,
  endpointById: apiEndpoints.ccisCodesById
});

/**
 * Remove.
 */

const remove = (): CrudRemoveType<CCIText> => ({
  endpoint: apiEndpoints.ccisCodesById
});

/**
 * Normalize payload.
 */

function normalizePayload(values: any) {
  const result = omit(values, ['id', 'createdAt', 'updatedAt']);

  result.cci = parseFloat(result.cci);

  return result;
}

/**
 * Add.
 */

const add = (translate: TFunction): CrudAddType<CCIText> => ({
  formFields: formFields(translate),
  acceptQueryFields: [
    {
      name: 'accountId',
      parser: value => (!!value ? parseInt(value, 10) : undefined)
    }
  ],
  normalizePayload
});

/**
 * Config.
 */

export function createCCIsCodesTemplate(translate: TFunction): CrudTemplate<CCIText> {
  return normalizeTemplate(translate, {
    add: add(translate),
    list: list(translate),
    remove: remove()
  });
}
